import * as Sentry from '@sentry/nextjs'
import {
  isApiTransportError,
  isAuthenticatedRequestUserMismatchError,
  isAuthRefreshUnavailableError,
  isMissingSessionError,
  isRequiresRealAccountError,
} from './apiErrors'
import { isHeicDecodeError } from './heicDecodeError'

export interface SentryErrorContext {
  operation: string
  component?: string
  userId?: string
  projectId?: string
  assetId?: string
  tags?: Record<string, string>
  extra?: Record<string, unknown>
  level?: 'fatal' | 'error' | 'warning' | 'info' | 'debug'
  fingerprint?: string[]
}

/**
 * Expected error types that should NOT be sent to Sentry
 */
class ExpectedError extends Error {
  constructor(
    message: string,
    public code?: string,
  ) {
    super(message)
    this.name = 'ExpectedError'
  }
}

/**
 * Validation error codes that are expected user errors
 */
const EXPECTED_VALIDATION_CODES = [
  'FILE_TOO_LARGE',
  'INVALID_FORMAT',
  'INVALID_DURATION',
  'TOKEN_EXPIRED', // Handled by auto-refresh
]

/**
 * Upload-specific validation error codes (subset of EXPECTED_VALIDATION_CODES)
 * Matches VideoValidationErrorCode type from uploadVideo.ts
 */
export const UPLOAD_VALIDATION_CODES = ['FILE_TOO_LARGE', 'INVALID_FORMAT', 'INVALID_MIME'] as const

function errorCodeOf(error: unknown): string | undefined {
  const code =
    error && typeof error === 'object'
      ? (error as { errorCode?: unknown; code?: unknown }).errorCode || (error as { code?: unknown }).code
      : undefined
  return typeof code === 'string' ? code : undefined
}

/**
 * Determine if an error should be captured to Sentry
 */
function shouldCapture(error: unknown): boolean {
  if (error instanceof ExpectedError) {
    return false
  }

  if (isRequiresRealAccountError(error)) {
    return false
  }

  // Refused before anything was sent because no session exists (another tab signed
  // out, storage cleared, a fetch that raced session creation). An expected state,
  // not broken code, and every authenticatedRequest caller can hit it, so it is
  // decided here once. A backend rejection of a request that was sent does not
  // match and still reports.
  if (isMissingSessionError(error)) {
    return false
  }

  // Reported once, at the transport choke point that knows the endpoint and
  // the retry count. Callers that catch it and report it again would each mint
  // their own group and scatter a single incident across the issue stream.
  if (isApiTransportError(error) && error.reportedToSentry) {
    return false
  }

  // Same choke-point rule: reported once by convertHeicFile, which knows why
  // the decoder failed; the upload surface that catches it next does not.
  if (isHeicDecodeError(error) && error.reportedToSentry) {
    return false
  }

  // Check for validation error codes
  const errorCode = errorCodeOf(error)
  if (errorCode && EXPECTED_VALIDATION_CODES.includes(errorCode)) {
    return false
  }

  return true
}

/**
 * Some errors describe expected safety or connectivity outcomes rather than
 * broken code: a refresh can be unavailable after a browser sleeps, and a
 * queued request can be rejected after the user switches accounts. They are
 * worth recording for visibility, but not at error level.
 *
 * Deliberately keyed on the error *type*, not on its `code` string: codes like
 * REFRESH_FAILED are generic enough that matching them would silently downgrade
 * unrelated callers that rely on the default error level.
 */
function defaultLevelFor(error: unknown): NonNullable<SentryErrorContext['level']> {
  if (isAuthRefreshUnavailableError(error) || isAuthenticatedRequestUserMismatchError(error)) return 'warning'
  return 'error'
}

/**
 * Capture an error to Sentry with structured context and filtering
 *
 * IMPORTANT: Wrapped in try-catch to ensure Sentry failures don't crash the platform
 */
export function captureSentryError(error: unknown, context: SentryErrorContext): void {
  try {
    // Filter out expected errors
    if (!shouldCapture(error)) {
      return
    }

    Sentry.withScope(scope => {
      // Set level (default: error, downgraded for known-transient failures)
      scope.setLevel(context.level || defaultLevelFor(error))

      // Set base tags
      scope.setTag('operation', context.operation)
      scope.setTag('layer', 'frontend')
      if (context.component) {
        scope.setTag('component', context.component)
      }

      // Add custom tags
      if (context.tags) {
        Object.entries(context.tags).forEach(([key, value]) => {
          scope.setTag(key, value)
        })
      }

      // Set fingerprint for grouping
      if (context.fingerprint) {
        scope.setFingerprint(context.fingerprint)
      }

      // Set context data
      const contextData: Record<string, unknown> = {}
      // Auto-populate userId from Sentry's current scope if not explicitly provided
      const userId = context.userId || Sentry.getCurrentScope().getUser()?.id
      if (userId) contextData.userId = userId
      if (context.projectId) contextData.projectId = context.projectId
      if (context.assetId) contextData.assetId = context.assetId

      if (Object.keys(contextData).length > 0) {
        scope.setContext('app', contextData)
      }

      // Add extra data
      if (context.extra) {
        Object.entries(context.extra).forEach(([key, value]) => {
          scope.setExtra(key, value)
        })
      }

      Sentry.captureException(error)
    })
  } catch (sentryError) {
    // Silently fail - don't let Sentry errors crash the platform
    // Optionally log to console for debugging
    console.error('[Sentry] Failed to capture error:', sentryError)
  }
}

// Export for use in filtering logic
export { ExpectedError }
